import { TATVA_AI } from "../../envConstants";
import { AUTH_TOKEN, FROM_TATAV_PRACTICE } from "../../utils/constants";
import { getLocalStorage } from "../../utils/localStorage";
import { useLocation, useNavigate } from "react-router-dom";
import { getAppType, getLabel } from "../../utils/appConfig";
import { getAiCredits } from "./service";
import { useEffect, useState } from "react";
import { ReactComponent as ArrowLeft } from "../../assets/images/arrow-left.svg";
import coin from "../../assets/images/tatvacoin.png";
import { navigateToTatvaPractice, sendMessageToParent } from "../../utils/helper";
import { getB2cIdWithFallbacks } from "../../utils/b2cHelper";
import { trackZymedEvent, useZymedOpenedEvent, ZYMED_EVENTS } from "../../utils/zymedAnalytics";

export default function TatvaAi() {
  const token = getLocalStorage(AUTH_TOKEN);
  const navigate = useNavigate();
  const { state } = useLocation();
  const [credits, setCredits] = useState<number>(0);
  useZymedOpenedEvent(ZYMED_EVENTS.AI_OPENED);

  useEffect(() => {
    getTatvaAiCredits();
  }, []);

  const handleSwipeGesture = (e: React.TouchEvent<HTMLDivElement>) => {
    const touchStartX = e.touches[0].clientX;
    const touchStartY = e.touches[0].clientY;
    const touchStartTime = Date.now();
    let maxVerticalMovement = 0;
    const handleTouchMove = (moveEvent: TouchEvent) => {
      const currentY = moveEvent.touches[0].clientY;
      const verticalDiff = Math.abs(currentY - touchStartY);
      maxVerticalMovement = Math.max(maxVerticalMovement, verticalDiff);
    };
    const handleTouchEnd = (endEvent: TouchEvent) => {
      const touchEndX = endEvent.changedTouches[0].clientX;
      const horizontalDiff = touchStartX - touchEndX;
      const duration = Date.now() - touchStartTime;
      const isLeftSwipe = horizontalDiff > 80;
      const isFastEnough = duration < 700;
      const isHorizontal = maxVerticalMovement < 60 || Math.abs(horizontalDiff) > maxVerticalMovement * 1.3;
      if (isLeftSwipe && isFastEnough && isHorizontal) {
        navigate('/', { replace: true });
      }
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
  };

  useEffect(() => {
    // Only the ZymedAI iframe may drive analytics; a message from any other window
    // (another iframe, an opener) must not be able to inject events.
    const aiOrigin = (() => {
      try {
        return new URL(TATVA_AI as string).origin;
      } catch {
        return null;
      }
    })();
    const handleMessage = (event: MessageEvent) => {
      const fromAi = !!aiOrigin && event.origin === aiOrigin;
      if (
        event.data === "CHAT_STREAM_COMPLETED" ||
        event.data?.type === "CHAT_STREAM_COMPLETED"
      ) {
        getTatvaAiCredits();
        // The chat runs in the tatva-ai iframe, which attaches query_id / query_text /
        // source_count / response_time_sec to this message. A failed stream is not an
        // answer, so it is not counted.
        const answerMeta: Record<string, unknown> =
          typeof event.data === "object" && event.data ? { ...event.data } : {};
        delete answerMeta.type;
        if (fromAi && answerMeta.status !== "error") {
          trackZymedEvent(ZYMED_EVENTS.AI_ANSWER_GENERATED, answerMeta);
        }
      }
      if (fromAi && event.data?.type === "ZYMED_TRACK_EVENT" && event.data?.event) {
        // Forward-compatible relay for events the iframe app will emit (ZymAI_Submit).
        trackZymedEvent(event.data.event, event.data.attributes || {});
      }
      // Handle source link clicks from iframe
      if (event.data?.type === "OPEN_LINK" && event.data?.url) {
        if (window.ReactNativeWebView) {
          // Send event to React Native wrapper to open link in ExternalLink screen with back button
          sendMessageToParent("OPEN_LINK", {
            url: event.data.url,
          });
        } else {
          // Fallback for web browsers (if React Native wrapper is not available)
          window.location.href = event.data.url;
        }
      }
    };

    window.addEventListener("message", handleMessage);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  const getTatvaAiCredits = async () => {
    const b2c_id = await getB2cIdWithFallbacks();
    const response: any = await getAiCredits(b2c_id);
    setCredits(response?.credit);
  };
  const handleHomeNavigation = () => {
    const fromPractice = getLocalStorage(FROM_TATAV_PRACTICE);
    if (fromPractice && fromPractice === "true") {
      navigateToTatvaPractice()
    } else {
      navigate("/")
    }
  }
  return (
    <div style={{ height: "100vh", position: "relative" }}>
      <div className="sticky top-0 px-4 py-3 bg-white flex flex-row items-center justify-between z-20">
        <div className="flex flex-row items-center gap-1.5 bg-white">
          <button onClick={handleHomeNavigation}>
            <ArrowLeft />
          </button>
          <h3 className="text-gray-800 font-semibold">{getLabel("aiLabel")}</h3>
        </div>
        {/* <div className="flex flex-row items-center gap-6 bg-white">
          <div className="w-full h-7 bg-orange-50 rounded-3xl border border-amber-500 border-solid backdrop-blur-[20px] items-center flex px-2 gap-1.5">
            <img
              loading="lazy"
              src={coin}
              alt="Coin"
              className="object-contain shrink-0 self-stretch my-auto aspect-[0.94] w-5"
            />
            <p className="font-semibold text-gray-800 text-[14px] mt-0.5 items-center">
              {credits ? String(credits).padStart(2, "0") : "0"}
            </p>
          </div>
        </div> */}
      </div>
      <div style={{ position: "relative", height: "calc(100% - 57px)" }}>
        <iframe
          src={
            state || `${TATVA_AI}/login?authToken=${token}&app=${getAppType()}`
          }
          title="Tatva AI"
          width="100%"
          height="100%"
          style={{ border: "none", position: "relative", zIndex: 1 }}
          allow="microphone; camera; clipboard-write; fullscreen; autoplay; geolocation"
        />
        {/*
          Edge strip that catches the swipe-back gesture, which the parent cannot see
          otherwise because touches inside a cross-origin iframe are not reported to it.

          It sits above the iframe, so anything it covers becomes untappable. It used to
          be `width: 10%` down to 84% height: on a tablet that is ~81px of the right edge,
          and the answer pane's audio and copy buttons sit about 40-60px in from that
          edge, so their taps were swallowed and copy appeared to do nothing. The
          `bottom: 16%` cut-out was an earlier workaround for the same problem hiding the
          text input.

          A percentage is the wrong unit here: an edge gesture zone should not grow with
          the screen. iOS system edge swipes use roughly 20pt at any size, so this is a
          fixed strip narrow enough to clear on-screen controls, which also lets the two
          pieces collapse back into one.
        */}
        <div
          onTouchStart={handleSwipeGesture}
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            width: "20px",
            zIndex: 10,
            touchAction: "pan-x",
          }}
          aria-hidden="true"
        />
      </div>
    </div>
  );
}